import React, { useState } from 'react';
import {
  usePopularTransformsQuery,
  useTransformCoverageQuery,
} from 'src/api/queries/deliveryAnalyticsQueries';
import {
  DeliveryAnalyticsFilters,
  TransformGroupBy,
} from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import { AsyncSection, DataAsOfBadge, Section } from '../components/common';
import styles from '../styles.module.scss';

const GROUP_BY: { value: TransformGroupBy; label: string }[] = [
  { value: 'spec', label: 'Full spec' },
  { value: 'resize', label: 'Resize' },
  { value: 'crop', label: 'Crop' },
  { value: 'format_delivered', label: 'Delivered format' },
  { value: 'quality_bucket', label: 'Quality bucket' },
];

const TransformationsTab: React.FC<{ filters: DeliveryAnalyticsFilters }> = ({ filters }) => {
  const [groupBy, setGroupBy] = useState<TransformGroupBy>('spec');
  const coverage = useTransformCoverageQuery(filters);
  const transforms = usePopularTransformsQuery({ ...filters, groupBy, limit: 100 });

  const cov = coverage.data;
  const rows = transforms.data?.data ?? [];
  const formats = cov ? Object.entries(cov.format_delivered) : [];

  return (
    <>
      <Section
        title="Transform coverage"
        description="On-the-fly (ODI) vs raw delivery, and the formats served."
        headerRight={<DataAsOfBadge value={cov?.data_as_of} loading={coverage.isLoading} />}
      >
        <AsyncSection
          isLoading={coverage.isLoading}
          error={coverage.error}
          isEmpty={!cov || cov.distinct_specs === 0}
        >
          {cov && (
            <>
              <div className={styles.statGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{cov.odi_pct.toFixed(1)}%</div>
                  <div className={styles.statLabel}>On-the-fly transformed</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{cov.raw_pct.toFixed(1)}%</div>
                  <div className={styles.statLabel}>Raw delivery</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>
                    {cov.avg_quality > 0 ? Math.round(cov.avg_quality) : '—'}
                  </div>
                  <div className={styles.statLabel}>Avg quality</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{formatNumberIntl(cov.distinct_specs)}</div>
                  <div className={styles.statLabel}>Distinct specs</div>
                </div>
              </div>
              {formats.length > 0 && (
                <div className={styles.formatChips}>
                  {formats
                    .sort((a, b) => b[1] - a[1])
                    .map(([fmt, count]) => (
                      <span key={fmt} className={styles.chip}>
                        {fmt || '(none)'}: {formatNumberIntl(count)}
                      </span>
                    ))}
                </div>
              )}
            </>
          )}
        </AsyncSection>
      </Section>

      <Section
        title="Popular transformations"
        description="Most-requested on-the-fly transform specs."
        headerRight={
          <div className={styles.inlineControls}>
            <select
              className={styles.select}
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as TransformGroupBy)}
            >
              {GROUP_BY.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            <DataAsOfBadge value={transforms.data?.data_as_of} loading={transforms.isLoading} />
          </div>
        }
      >
        <AsyncSection
          isLoading={transforms.isLoading}
          error={transforms.error}
          isEmpty={rows.length === 0}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Transform</th>
                <th className={styles.numCol}>Requests</th>
                <th className={styles.numCol}>Bytes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.group || '(none)'}>
                  <td className={styles.mono}>{r.group || '(none)'}</td>
                  <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
                  <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AsyncSection>
      </Section>
    </>
  );
};

export default TransformationsTab;
